import React from 'react';
import {
  VStack, Card, Heading, Box, Text, HStack, Icon,
} from '@gluestack-ui/themed';
import { Lock } from 'lucide-react-native';
import { useForm, FormProvider } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';

import {
  formatEnglishDateLong,
  formatEnglishDateWithYear,
  formatEnglishTime,
} from '@/common/utils/formatEnglishDateTime';

import { useAuthStore } from '@/common/auth/store';
import { useDialog } from '@/common/context/DialogContext';
import { useGuardStudentAction, useStudentPreviewSource } from '@/common/studentPreview/StudentPreviewSource';
import { useRefetchModuleTasks } from '@/common/hooks/student/useRefetchModuleTasks';
import { useTenantEventKinds, useTenantSettingsStore } from '@/common/context/TenantSettingsStore';
import { OralExamFactsSummary } from '@/common/components/OralExamFactsSummary';
import { useUpdateSubmission } from '@/common/hooks/student/task/useUpdateSubmission';
import { isOralExamPracticeTask } from '@/common/utils/oralExam/practiceApi';
import { isOralExamTaken } from '@/common/utils/oralExam/examTaken';
import { resolveEventKindRequiresRecording } from '@/common/utils/taskEvaluationUtils';

import { Task, Module, Resource } from '@/student/types';
import { OralExamMobileAppNotice } from '@/common/components/oralExam/OralExamMobileAppNotice';
import { useUserFeatures } from '@/common/hooks/useUserFeatures';

import { OralExamPracticeEntry } from '@/student/course/components/OralExamPracticeEntry';
import { useEventTaskSlots } from '../../../hooks/useEventTaskSlots';
import EventTaskDescription from './components/EventTaskDescription';
import EventTaskLockedNotice from './components/EventTaskLockedNotice';
import EventSessionCompleted from './components/EventSessionCompleted';
import EventSlotSelection from './components/EventSlotSelection';
import EventBookingDetails from './components/EventBookingDetails';
import EventResources from './components/EventResources';
import EventTaskCompletedView from './components/EventTaskCompletedView';
import EventTaskFooterActions from './components/EventTaskFooterActions';
import ExamPreflightChecklist from './components/ExamPreflightChecklist';
import { OralExamBeforeExamChecklist } from './components/OralExamBeforeExamChecklist';
import { OralExamRegistrationCompleted } from './components/OralExamRegistrationCompleted';
import { OralExamRegistrationCancelled } from './components/OralExamRegistrationCancelled';
import ModuleTaskPreviewErrorBoundary from '../../ModuleTaskPreviewErrorBoundary';
import { OralExamQuestionPoolSection } from '../../OralExamQuestionPoolSection';
import { GssPeopleSection } from './components/GssPeopleSection';

interface EventTaskViewProps {
  task: Task;
  loading?: boolean;
  module: Module;
  tasks?: Task[];
  selectedModule: { courseId: string; moduleId: string };
  readOnly?: boolean;
  showTitle?: boolean;
  resources?: Resource[];
  noCard?: boolean;
  handleOpenTask?: () => void;
  /**
   * ``teaser`` (default) — three readable rows + exclusive accordion on the module preview.
   * ``full`` — entire pool always expanded to bullets (TaskScreen).
   * ``hidden`` — pool lives in a sibling column (desktop TaskScreen prep).
   */
  poolSurface?: 'teaser' | 'full' | 'hidden';
  [key: string]: unknown;
}

/** Titled section on graded oral exams; other event types render the session untitled. */
function RegistrationSection({ title, children }: { title?: string; children: React.ReactNode }) {
  if (!title) return <VStack>{children}</VStack>;
  return (
    <VStack space="sm" testID="oral-exam-registration-section">
      <Heading size="sm">{title}</Heading>
      {children}
    </VStack>
  );
}

/**
 * EventTaskView
 * -------------
 * Displays the details of an event task, including:
 * - Task description and resources
 * - Booking status (if any)
 * - Slot selection (if booking available)
 * - Completion status
 *
 * Graded oral exams follow the column order of use: About this exam →
 * Your registration → Before your exam (until taken; its last step opens the
 * question pool) → lecturer notes and materials → dry run.
 */
const EventTaskView: React.FC<EventTaskViewProps> = ({
  task,
  loading,
  module,
  tasks = [],
  selectedModule,
  readOnly = false,
  showTitle = true,
  resources = [],
  noCard = false,
  handleOpenTask,
  poolSurface = 'teaser',
  ...props
}) => {
  const { t } = useTranslation();
  const { showToastMessage, showDialog } = useDialog();
  const guardStudentAction = useGuardStudentAction();
  const isPreviewSourceActive = useStudentPreviewSource();
  const isTrialStudent = useAuthStore((state) => Boolean(state.user?.trial?.isTrial)) && !isPreviewSourceActive;
  const { isFeatureActive } = useUserFeatures();
  const supportEmail = useTenantSettingsStore((s) => s.settings?.contact?.supportEmail);
  const { getEventKindById } = useTenantEventKinds();
  const formMethods = useForm({ defaultValues: { slotId: null } });

  // 1. Extract Key IDs
  const courseId = selectedModule?.courseId || module?.courses?.[0]?.id;
  const moduleId = module?.id;
  const moduleVersion = module?.version;
  const refetchModuleTasks = useRefetchModuleTasks(courseId || '', moduleId || '');
  const handleRetryQuestionPool = courseId && moduleId
    ? () => guardStudentAction(() => { refetchModuleTasks(); })
    : undefined;

  // 2. Determine High-Level States
  const isTaskLocked = task?.eventConfig?.locked;
  const isTaskCompleted = task.submission?.state === 'marked_as_done';

  // 3. Setup Hooks
  const [shouldPollBookings, setShouldPollBookings] = React.useState(false);
  const [isBookingAgain, setIsBookingAgain] = React.useState(false);
  const [fastExamPoll, setFastExamPoll] = React.useState(false);

  const {
    slots,
    slotsLoading,
    slotsError,
    currentBooking,
    cancelledBooking,
    meetingUrl,
    examSessionStatus,
    bookSlot,
    cancelBooking,
    bookingPending,
  } = useEventTaskSlots(courseId, moduleId, moduleVersion, task.id, {
    enabled: !isTaskLocked
      && (!isTaskCompleted || isFeatureActive('student-connections'))
      && !isOralExamPracticeTask(task),
    pollSlots: true,
    pollBookings: shouldPollBookings,
    examSessionPollMs: fastExamPoll ? 3_000 : undefined,
    task,
  });
  const updateSubmissionMutation = useUpdateSubmission({ courseId: courseId || '', moduleId: moduleId || '' });

  // 4. Derived States
  const selectedSlotId = formMethods.watch('slotId');
  const completionPolicy = task?.evaluation?.completionPolicyEffective;
  const eventKindId = task?.eventConfig?.eventKindId;
  const isManualReleaseEvent = Boolean(eventKindId && getEventKindById(eventKindId)?.requiresManualRelease);
  const isSessionCompleted = currentBooking?.status === 'completed';
  const hasActiveBooking = !!currentBooking;
  const canCancelBooking = currentBooking && dayjs(currentBooking.startTime).diff(dayjs(), 'hours') >= 24;
  // Oral exams are a single, graded exam session: they must never offer the generic
  // event-task escape hatches ("register for another session" / "mark as done").
  // Those belong to optional events like group study sessions.
  const isOralExam = task.type === 'oral_exam';
  const viewerRegisteredForSession = !!currentBooking && currentBooking.status !== 'cancelled';
  const showGssPeopleSection = isFeatureActive('student-connections')
    && viewerRegisteredForSession
    && !isOralExam;
  const gssPeopleSlotId = currentBooking?.slotId;
  const isPracticeOralExam = isOralExamPracticeTask(task);

  // Derive exam gate states from the enriched session status endpoint
  // so they update on the faster exam session poll, not the slower booking poll.
  const isBookingAdmitted = examSessionStatus?.admitted === true;
  const isExamOpen = !!examSessionStatus?.openedAt && !examSessionStatus?.closedAt;
  const isExamClosed = !!examSessionStatus?.closedAt;
  // Recompute on every render (triggered by exam session polls) so the
  // preflight window opens in real-time without waiting for booking refetch.
  const isInPreflightWindow = (() => {
    if (!currentBooking || !isManualReleaseEvent) return false;
    const now = dayjs();
    const start = dayjs(currentBooking.startTime);
    const end = dayjs(currentBooking.endTime);
    return now.isAfter(start.subtract(10, 'minute')) && now.isBefore(end.add(10, 'minute'));
  })();
  const shouldShowPreflight = isManualReleaseEvent
    && hasActiveBooking
    && (currentBooking?.status === 'confirmed' || currentBooking?.status === 'pending')
    && (isInPreflightWindow || isExamOpen);

  const trialBlocksNewRegistration = isTrialStudent && !hasActiveBooking && !isBookingAgain;
  const showRegisterAgain = isSessionCompleted && !isBookingAgain && !isOralExam && !isTrialStudent;
  const showMarkDoneLink = completionPolicy === 'free'
    && !isTaskLocked
    && !isTaskCompleted
    && !isManualReleaseEvent
    && !isOralExam
    && (!hasActiveBooking || isSessionCompleted);

  // 5. Effects
  React.useEffect(() => {
    const next = Boolean(currentBooking);
    if (next !== shouldPollBookings) setShouldPollBookings(next);
  }, [currentBooking, shouldPollBookings]);

  React.useEffect(() => {
    const shouldFastPoll = isInPreflightWindow || isExamOpen;
    if (shouldFastPoll !== fastExamPoll) setFastExamPoll(shouldFastPoll);
  }, [isInPreflightWindow, isExamOpen, fastExamPoll]);

  React.useEffect(() => {
    if (currentBooking?.status !== 'completed') {
      setIsBookingAgain(false);
    }
  }, [currentBooking]);

  // 6. Handlers
  const handleBook = async () => {
    if (!selectedSlotId) return;

    const selectedSlot = slots.find((slot) => slot.slotId === selectedSlotId);
    const eventKindId = selectedSlot?.eventKindId ?? task?.eventConfig?.eventKindId;
    const eventKind = eventKindId ? getEventKindById(eventKindId) : undefined;
    const isRecordingRequired = eventKind ? resolveEventKindRequiresRecording(eventKind) : false;

    const formatDate = (d: string) => formatEnglishDateWithYear(d);
    const formatTime = (d: string) => formatEnglishTime(d);

    showDialog({
      title: t('student.eventTask.registrationConfirmationTitle'),
      message: (() => {
        const base = t('student.eventTask.registrationConfirmationBase', {
          date: selectedSlot ? formatDate(selectedSlot.startTime) : '',
          time: selectedSlot ? formatTime(selectedSlot.startTime) : '',
        });
        if (!isRecordingRequired) return base;
        const notice = task?.eventConfig?.recordingConsentText || t('student.eventTask.registrationRecordingNotice');
        return `${base}\n\n${notice}`;
      })(),
      buttons: [
        { text: t('common.buttons.cancel'), variant: 'outline', action: 'secondary' },
        {
          text: t('student.eventTask.confirmRegistration'),
          action: 'positive',
          onPress: async () => {
            try {
              const recordingConsentGiven = isRecordingRequired ? true : undefined;
              await bookSlot(selectedSlotId, recordingConsentGiven);
              if (!isPreviewSourceActive) {
                setShouldPollBookings(true);
                showToastMessage('success', t('student.eventTask.registrationSuccess'));
              }
            } catch (err) {
              showToastMessage('error', t('student.eventTask.registrationError'));
            }
          },
        },
      ],
    });
  };

  const handleCancel = () => {
    if (!currentBooking) return;
    const bookingDate = formatEnglishDateLong(currentBooking.startTime);
    const bookingTime = formatEnglishTime(currentBooking.startTime);
    showDialog({
      title: t('student.eventTask.cancelConfirmationTitle'),
      message: t('student.eventTask.cancelConfirmation', {
        date: bookingDate,
        time: bookingTime,
      }),
      buttons: [
        { text: t('student.eventTask.abort'), variant: 'outline', action: 'secondary' },
        {
          text: t('student.eventTask.cancelButton'),
          action: 'negative',
          onPress: async () => {
            try {
              await cancelBooking(currentBooking.bookingId);
              if (!isPreviewSourceActive) {
                showToastMessage('success', t('student.eventTask.cancelSuccess'));
              }
            } catch (err) {
              showToastMessage('error', t('student.eventTask.cancelError'));
            }
          },
        },
      ],
    });
  };

  const handleMarkDone = () => {
    showDialog({
      title: t('student.eventTask.markDoneConfirmTitle', 'Mark as done?'),
      message: t('student.eventTask.markDoneConfirmMessage', 'You can undo this later.'),
      buttons: [
        { text: t('common.buttons.cancel'), variant: 'outline', action: 'secondary' },
        {
          text: t('student.eventTask.markDoneLink', 'Mark as done'),
          action: 'positive',
          onPress: async () => {
            await guardStudentAction(async () => {
              try {
                if (courseId && moduleId) {
                  await updateSubmissionMutation.mutateAsync({
                    courseId,
                    moduleId,
                    taskId: task.id,
                    data: { text: task?.submission?.text || '', state: 'marked_as_done' },
                  });
                  showToastMessage('success', t('student.eventTask.markDoneSuccess', 'Marked as done'));
                }
              } catch (e) {
                showToastMessage('error', t('student.eventTask.markDoneError', 'Could not mark as done'));
              }
            });
          },
        },
      ],
    });
  };

  const handleUnmarkDone = async () => {
    await guardStudentAction(async () => {
      try {
        if (courseId && moduleId) {
          await updateSubmissionMutation.mutateAsync({
            courseId,
            moduleId,
            taskId: task.id,
            data: { text: task?.submission?.text || '', state: 'draft' },
          });
          setIsBookingAgain(true); // Automatically show slot selection after unmarking
          showToastMessage('success', t('student.eventTask.unmarkSuccess', 'You can register for another session now'));
        }
      } catch (e) {
        showToastMessage('error', t('student.eventTask.unmarkError', 'Could not enable re-registration'));
      }
    });
  };

  // 7. Render Flow
  const ContentWrapper = noCard ? VStack : Card;
  const wrapperProps = noCard ? { ...props } : { ...props, testID: 'event-task-card' };
  const isGradedOralExam = isOralExam && !isPracticeOralExam;
  const isExamTaken = isOralExamTaken(task, currentBooking);
  const opensQuestionsPage = poolSurface === 'teaser' && !!handleOpenTask;
  const hasSessionContent = isTaskLocked || isTaskCompleted || !isPracticeOralExam;
  let sectionSpace: 'md' | 'lg' | 'xl' = noCard ? 'md' : 'lg';
  if (isOralExam) sectionSpace = 'xl';

  const descriptionSection = (
    <ModuleTaskPreviewErrorBoundary task={task} module={module} label="EventTaskDescription">
      <EventTaskDescription
        description={task.description || ''}
        isLocked={isTaskLocked || false}
        isOralExam={isOralExam}
      />
    </ModuleTaskPreviewErrorBoundary>
  );
  const resourcesSection = (
    <ModuleTaskPreviewErrorBoundary task={task} module={module} label="EventResources">
      <EventResources resources={resources} isOralExam={isOralExam} />
    </ModuleTaskPreviewErrorBoundary>
  );

  return (
    <ContentWrapper {...wrapperProps}>
      <VStack space={sectionSpace}>
        {showTitle && (
          <Heading size="lg">{String(task.title ?? '')}</Heading>
        )}

        {task.type === 'oral_exam' && !isPracticeOralExam && !!module?.examAttempt?.latestFailureReason && (
          <Box
            p="$3"
            bg="$backgroundLight50"
            borderRadius="$md"
            testID="exam-attempt-last-reason"
          >
            <Text size="sm" color="$textBody">
              {t('student.eventTask.lastAttemptFailed', {
                defaultValue: 'Your last attempt counted as failed: {{sentence}}',
                sentence: module.examAttempt.latestFailureReason,
              })}
            </Text>
          </Box>
        )}
        {task.type === 'oral_exam' && !isPracticeOralExam && (
          <OralExamFactsSummary
            resolvedQuestionCount={task.oralExamConfig?.resolvedQuestionCount}
            resolvedSecondsPerQuestion={task.oralExamConfig?.resolvedSecondsPerQuestion}
            poolSize={task.oralExamConfig?.questionPool?.length ?? 0}
            variant="compact"
          />
        )}
        {!isOralExam && descriptionSection}
        {!isOralExam && resourcesSection}

        {isOralExam && (
          <ModuleTaskPreviewErrorBoundary task={task} module={module} label="OralExamMobileAppNotice">
            <OralExamMobileAppNotice />
          </ModuleTaskPreviewErrorBoundary>
        )}

        {/* SESSION — the task. Register / join sits above preparation. */}
        {hasSessionContent && (
        <RegistrationSection title={isGradedOralExam ? t('oralExam.registration.title') : undefined}>
          {isTaskLocked && (
          <VStack testID="event-session-section">
            <ModuleTaskPreviewErrorBoundary task={task} module={module} label="EventTaskLockedNotice">
              <EventTaskLockedNotice task={task} tasks={tasks} module={module} />
            </ModuleTaskPreviewErrorBoundary>
          </VStack>
          )}

          {/* ACTIVE STATE (Not Locked, Not Completed) */}
          {!isTaskLocked && !isTaskCompleted && !isPracticeOralExam && (
          <VStack
            space="md"
            pt={isGradedOralExam ? '$0' : '$4'}
            borderTopWidth={isGradedOralExam ? 0 : 1}
            borderTopColor="$borderLight200"
            testID="event-session-section"
          >
            <FormProvider {...formMethods}>
              {isSessionCompleted && !isBookingAgain ? (
                <ModuleTaskPreviewErrorBoundary task={task} module={module} label="EventSessionCompleted">
                  {isGradedOralExam ? (
                    <OralExamRegistrationCompleted attendedAtIso={currentBooking?.startTime} />
                  ) : (
                    <EventSessionCompleted
                      slotId={currentBooking?.slotId}
                      showPeopleSection={showGssPeopleSection}
                    />
                  )}
                </ModuleTaskPreviewErrorBoundary>
              ) : hasActiveBooking && !isBookingAgain ? (
                <ModuleTaskPreviewErrorBoundary task={task} module={module} label="EventBookingDetails">
                  <VStack space="lg">
                    <EventBookingDetails
                      booking={currentBooking}
                      meetingUrl={meetingUrl}
                      canCancel={canCancelBooking}
                      onCancel={handleCancel}
                      bookingPending={bookingPending}
                      isTaskCompleted={false}
                    />
                    {showGssPeopleSection && gssPeopleSlotId ? (
                      <GssPeopleSection slotId={gssPeopleSlotId} />
                    ) : null}
                  </VStack>
                </ModuleTaskPreviewErrorBoundary>
              ) : trialBlocksNewRegistration ? (
                <HStack
                  testID="trial-read-only-notice"
                  alignItems="center"
                  space="sm"
                  p="$3"
                  bg="$bgSubtle"
                  borderRadius="$md"
                >
                  <Icon as={Lock} size="sm" color="$textSubtle" />
                  <Text flex={1} size="sm" color="$textBody">
                    {t('student.trial.readOnlyNotice')}
                  </Text>
                </HStack>
              ) : (
                <ModuleTaskPreviewErrorBoundary task={task} module={module} label="EventSlotSelection">
                  <VStack space="md">
                    {isGradedOralExam && cancelledBooking && !isBookingAgain && (
                      <OralExamRegistrationCancelled startIso={cancelledBooking.startTime} />
                    )}
                    <EventSlotSelection
                      slots={slots}
                      loading={slotsLoading}
                      error={slotsError}
                      supportEmail={supportEmail}
                      onBook={handleBook}
                      bookingPending={bookingPending}
                      isBookingAgain={isBookingAgain}
                      onCancelBookingAgain={() => setIsBookingAgain(false)}
                      moduleId={moduleId || ''}
                      insideSection={isGradedOralExam}
                    />
                  </VStack>
                </ModuleTaskPreviewErrorBoundary>
              )}
            </FormProvider>
            {shouldShowPreflight && (
              <ModuleTaskPreviewErrorBoundary task={task} module={module} label="ExamPreflightChecklist">
                <ExamPreflightChecklist
                  meetingUrl={meetingUrl}
                  isBookingAdmitted={isBookingAdmitted}
                  isExamOpen={isExamOpen}
                  isExamClosed={isExamClosed}
                  onStartQuiz={handleOpenTask}
                />
              </ModuleTaskPreviewErrorBoundary>
            )}
            <ModuleTaskPreviewErrorBoundary task={task} module={module} label="EventTaskFooterActions">
              <EventTaskFooterActions
                showRegisterAgain={showRegisterAgain}
                onRegisterAgain={() => setIsBookingAgain(true)}
                showMarkDone={showMarkDoneLink}
                onMarkDone={handleMarkDone}
                markDoneLoading={updateSubmissionMutation.isPending}
                markDoneLabel={
                  isSessionCompleted
                    ? t('student.eventTask.markDoneButton', 'Mark Task as Done')
                    : t('student.eventTask.markDoneLink', 'Skip event, mark as done')
                }
              />
            </ModuleTaskPreviewErrorBoundary>
          </VStack>
          )}

          {/* COMPLETED STATE */}
          {isTaskCompleted && !isTaskLocked && (
          <VStack space="lg">
            <ModuleTaskPreviewErrorBoundary task={task} module={module} label="EventTaskCompletedView">
              {isGradedOralExam ? (
                <OralExamRegistrationCompleted attendedAtIso={currentBooking?.startTime} />
              ) : (
                <EventTaskCompletedView
                  readOnly={readOnly}
                  isLoading={updateSubmissionMutation.isPending}
                  onUnmarkDone={isOralExam || isTrialStudent ? undefined : handleUnmarkDone}
                  isProctoredExam={isManualReleaseEvent}
                />
              )}
            </ModuleTaskPreviewErrorBoundary>
            {showGssPeopleSection && gssPeopleSlotId ? (
              <GssPeopleSection slotId={gssPeopleSlotId} />
            ) : null}
          </VStack>
          )}
        </RegistrationSection>
        )}

        {isGradedOralExam && !isExamTaken && (
          <ModuleTaskPreviewErrorBoundary task={task} module={module} label="OralExamBeforeExamChecklist">
            <OralExamBeforeExamChecklist
              bookingStartIso={
                hasActiveBooking && !isBookingAgain && currentBooking?.status !== 'cancelled'
                  ? currentBooking.startTime
                  : null
              }
              questionCount={task.oralExamConfig?.questionPool?.length ?? 0}
              onOpenQuestions={opensQuestionsPage ? handleOpenTask : undefined}
              dryRunTaskId={isTaskLocked ? undefined : task.id}
            />
          </ModuleTaskPreviewErrorBoundary>
        )}

        {isOralExam && descriptionSection}
        {isOralExam && resourcesSection}

        {/* PREPARE — the full pool on the mobile task page. On the module preview the
            checklist's button opens the task page instead. */}
        {isGradedOralExam && !isExamTaken && poolSurface !== 'hidden' && !opensQuestionsPage && (
          <ModuleTaskPreviewErrorBoundary task={task} module={module} label="OralExamQuestionPoolSection">
            <OralExamQuestionPoolSection
              task={task}
              surface={poolSurface === 'full' ? 'full' : 'teaser'}
              onRetry={handleRetryQuestionPool}
            />
          </ModuleTaskPreviewErrorBoundary>
        )}

        {/* A dry-run task is the dry run itself; graded exams offer it in "Before your exam". */}
        {isPracticeOralExam && !isTaskLocked && (
          <ModuleTaskPreviewErrorBoundary task={task} module={module} label="OralExamPracticeEntry">
            <OralExamPracticeEntry taskId={task.id} />
          </ModuleTaskPreviewErrorBoundary>
        )}

      </VStack>
    </ContentWrapper>
  );
};

export default EventTaskView;
